import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  getDefaultMentionCategories,
  normalizeMentionSearchResponse,
  type MentionSearchResult,
} from 'features/chat/utils/normalize-mention-search';
import { useDebounce } from 'hooks/use-debounce';
import { useConfigStore } from 'state/store/config/useConfigStore';
import {
  MENTION_SEARCH_TYPES_PARAM,
  type MentionSearchType,
} from '../types/mentions.service.type';
import { mentionsService } from '../services/mentions.service';

const CHAT_MENTION_SEARCH_KEY = 'mentions-chat-search';
const MENTION_SEARCH_DEBOUNCE_MS = 300;

export const CHAT_MENTION_SEARCH_TYPES: MentionSearchType[] = [...MENTION_SEARCH_TYPES_PARAM];

const EMPTY_RESULT: MentionSearchResult = {
  items: [],
  categories: getDefaultMentionCategories(),
};

function applyTypesFilter(
  result: MentionSearchResult,
  typesFilter?: MentionSearchType[] | null
): MentionSearchResult {
  if (!typesFilter?.length) return result;

  const allowed = new Set(typesFilter);
  return {
    categories: result.categories,
    items: result.items.filter((item) => allowed.has(item.type)),
  };
}

export function useChatMentionSearch(
  search: string,
  enabled: boolean,
  typesFilter?: MentionSearchType[] | null,
  options?: { surface?: string; limit?: number }
) {
  const projectKey = useConfigStore((state) => state.configurationSettings?.blocksKey || '');
  const debouncedSearch = useDebounce(search, MENTION_SEARCH_DEBOUNCE_MS);
  const effectiveSearch = search.trim() ? debouncedSearch : search;
  const surface = options?.surface;
  const limit = options?.limit;
  const query = useQuery({
    queryKey: [CHAT_MENTION_SEARCH_KEY, projectKey, effectiveSearch, CHAT_MENTION_SEARCH_TYPES, surface, limit],
    queryFn: async () => {
      const response = await mentionsService.search(
        {
          q: effectiveSearch.trim() || undefined,
          types: CHAT_MENTION_SEARCH_TYPES,
          surface,
          limit,
        },
        projectKey
      );
      return normalizeMentionSearchResponse(response);
    },
    enabled: enabled && !!projectKey,
    staleTime: 0,
    placeholderData: (previous) => previous ?? EMPTY_RESULT,
    select: (data) => applyTypesFilter(data, typesFilter),
  });

  const wasEnabledRef = useRef(false);

  useEffect(() => {
    const justOpened = enabled && !wasEnabledRef.current;
    wasEnabledRef.current = enabled;

    if (justOpened && projectKey) {
      void query.refetch();
    }
  }, [enabled, projectKey, query.refetch]);

  const isCategoriesView = effectiveSearch.trim().length === 0;

  return {
    ...query,
    isCategoriesLoading: isCategoriesView && query.isFetching,
  };
}
